/**
 * Asynchronously map an array of values to an array of resolved values
 * @param {readonly T[]} array
 * @param {(value: T, index: number, array: readonly T[]) => Promise<U>} callbackFn
 * @param thisArg
 * @returns {Promise<U[]>}
 */
export const asyncMap = <T, U>(
	array: readonly T[],
	callbackFn: (value: T, index: number, array: readonly T[]) => Promise<U>,
	thisArg?: any,
): Promise<U[]> => Promise.all(array.map(callbackFn, thisArg));

/**
 * Asynchronously filter an array of values
 * @param {readonly T[]} array
 * @param {(value: T, index: number, array: readonly T[]) => Promise<boolean>} predicate
 * @param thisArg
 * @returns {Promise<T[]>}
 */
export const asyncFilter = async <T>(
	array: readonly T[],
	predicate: (value: T, index: number, array: readonly T[]) => Promise<boolean>,
	thisArg?: any,
): Promise<T[]> => {
	const resolvedPredicates = await asyncMap(array, predicate, thisArg);
	return array.filter((item, idx) => resolvedPredicates[idx]);
};

/**
 * Async version of Array.prototype.forEach
 * @param array Array to iterate
 * @param callback Callback function to execute for each item
 * @returns A Promise that resolves when all items have been processed
 */
export const asyncForEach = async <T>(array: T[], callback: (item: T, index: number, array: T[]) => Promise<void>) => {
	// eslint-disable-next-line no-plusplus
	for (let index = 0; index < array.length; index++) {
		await callback(array[index]!, index, array);
	}
};

/**
 * Resolves generated promise with optional value
 * @param delay Delay in milliseconds
 * @param resolver Callback function to execute before resolving the promise
 * @param value Optional value to resolve the promise with
 * @returns Promise that resolves after the specified delay
 */
export const fakePromise = (delay: number, resolver?: () => any, value?: any) =>
	new Promise((resolve) =>
		// eslint-disable-next-line no-promise-executor-return
		setTimeout(
			() => {
				resolver?.();
				resolve(value);
			},
			delay,
			value,
		),
	);

/**
 * Resolved an input promise within a minimum delay time. It may be useful for showing a loading spinner for a minimum amount of time.
 * @param {PromiseLike<T>} promise
 * @param {number} delay
 * @returns {Promise<Awaited<T>>}
 */
export const minimumPromise = async <T>(promise: PromiseLike<T>, delay: number = 500): Promise<Awaited<T>> =>
	(await Promise.all([promise, fakePromise(delay)]))[0];
